// The door-side readers of the four app facts `configureEvidenceData` injects
// for the collections carve — the authentication verdict (ruling 65,
// `docs/monorepo-ui-floor-rulings/65-identity-crosses-by-injection.md`), the
// family's two flag gates, and the source catalogue.
//
// WHY A MODULE RATHER THAN `getEvidenceDataConfig()` AT EACH CALL SITE. The
// flag and catalogue reads are hooks the rules-of-hooks lint cannot see as a
// member call. Naming them here gives every reader an ordinary `useX()` call.
// The authentication verdict reads `@heidi/auth/state`.
//
// Headless by construction: no design system, no router, no store, no `@/`.
import { useIsAuthenticated as useAuthIdentity } from '@heidi/auth/state';

import type {
  ChatSourceItem,
  LibraryAnalyticsEvent,
  LibraryChatErrorReport,
} from '../contracts';
import type { EvidenceDataConfig } from './config';
import { getEvidenceDataConfig } from './config';

export const areCapabilitiesReportedByBackend = (): boolean =>
  getEvidenceDataConfig().capabilitiesReportedByBackend;

/** The provider-aware authentication verdict, from the auth identity hook. */
export const useIsAuthenticated = (): boolean => useAuthIdentity().isAuthenticated;

/**
 * `IS_EVIDENCE_LIBRARY_ENABLED()`, read at the same moment it was read before
 * the carve — inside the hook body, every render.
 *
 * IT RETURNS `boolean | undefined` AND THAT IS THE CONTRACT, not a loose type.
 * The app getter is `posthog.isFeatureEnabled(…) && …`, which is `undefined`
 * while the PostHog client is still loading, and the legacy gate expression
 * therefore evaluated to `undefined` for a signed-in user in that window —
 * which react-query reads as its DEFAULT, i.e. ENABLED. Narrowing this to
 * `boolean` here (or writing `!!` at the wiring) would suppress a request the
 * app makes today, on every cold load, for as long as the flag client takes to
 * resolve. `data/query/sourceListQueries.ts` records the same tri-state on the
 * `enabled` option it has carried since the D2 slice-2 carve.
 */
export const isEvidenceLibraryEnabled = (): boolean | undefined =>
  getEvidenceDataConfig().isEvidenceLibraryEnabled();

/**
 * `IS_UNIFIED_COLLECTIONS_ENABLED()`, same timing.
 *
 * This one really is a `boolean`: it resolves through OpenFeature's
 * `getBooleanValue(key, false)` behind a region check, so it has no loading
 * window to represent.
 */
export const isUnifiedCollectionsEnabled = (): boolean =>
  getEvidenceDataConfig().isUnifiedCollectionsEnabled();

/** The static source catalogue, with the app's on-prem surface gate applied. */
export const useSourceCatalogue = (): {
  data: ChatSourceItem[] | undefined;
  isLoading: boolean;
} => getEvidenceDataConfig().useSourceCatalogue();

/**
 * `IS_EVIDENCE_USER_DOCS_ENABLED()`, same timing and same tri-state as
 * `isEvidenceLibraryEnabled` — see that getter for why the `undefined` is
 * the contract rather than a loose type.
 */
export const isEvidenceUserDocsEnabled = (): boolean | undefined =>
  getEvidenceDataConfig().isEvidenceUserDocsEnabled();

/** The app's composite gamify-trial invalidation, after an upload settles. */
export const invalidateGamifyTrialExtension = (): Promise<void> =>
  getEvidenceDataConfig().invalidateGamifyTrialExtension();

/** Emits one of this domain's six library analytics events. */
export const trackLibraryEvent = (event: LibraryAnalyticsEvent): void =>
  getEvidenceDataConfig().trackLibraryEvent(event);

/** The app's chat-error reporter, resolved per render (see `config.ts`). */
export const useTrackChatError = (): ((report: LibraryChatErrorReport) => void) =>
  getEvidenceDataConfig().useTrackChatError();

/** `IS_BULK_UPLOAD_ENABLED()`, read at the moment the dropzone renders. */
export const isBulkUploadEnabled = (): boolean =>
  getEvidenceDataConfig().isBulkUploadEnabled();

/** react-pdf's `Document`/`Page`, with the app's worker path already set. */
export const getReactPdf = (): EvidenceDataConfig['reactPdf'] =>
  getEvidenceDataConfig().reactPdf;

/** The active region, read where the source-search helpers read it today. */
export const getRegion = (): string => getEvidenceDataConfig().getRegion();

/** The clinician's evidence-settings write (see `config.ts` for the hook rule). */
export const useUpdateEvidenceSettings = (): ReturnType<
  EvidenceDataConfig['useUpdateEvidenceSettings']
> => getEvidenceDataConfig().useUpdateEvidenceSettings();
